/**
 * 华为应用商店 → 策略运行记录（CLAUDE.md §前端 UI 须遵循 LEO v1.0；本页复用 leo-* 类）。
 *
 * 已读 leo-frontend-design.md v1.0，适用章节：§4.2 滚动内容页、§6.3 表单控件、
 * §6.5 表格/列表、§7.2 反馈原则、§7.3 日期格式（YYYY-MM-DD）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/huawei/auto-ad-operate-result/enums 取下拉枚举 +
 *     GET /api/v1/huawei/auto-ad-operate-result 取首页数据（无条件）。
 *   - 筛选：ds 起止(date) / 任务ID 模糊 / 任务名称模糊 / 操作类型下拉 /
 *     操作原因模糊；点「查询」提交（page 重置为 1）。
 *   - 分页：服务端分页（上一页/下一页 + 总数/总页数）。
 *   - 数据来源：t_huawei_auto_ad_operate_result（huawei_red_check 产出动作落库），
 *     含媒体交互状态（status）/ 媒体响应报文（message；华为 update_ad 尚未实现时为空）。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type AutoAdOperateResultEnums,
  type AutoAdOperateResultPage,
  type AutoAdOperateResultQuery,
  type AutoAdOperateResultRecord,
  getAutoAdOperateResult,
  getAutoAdOperateResultEnums,
} from "../../services/huawei/autoAdOperateResult";
import { pushToast } from "../../hooks/useToast";

/** 筛选表单输入态（空串表示「不限」）。 */
interface LogFilters {
  ds_start: string;
  ds_end: string;
  taskId: string;
  taskName: string;
  action_name: string;
  condition: string;
}

const EMPTY_FILTERS: LogFilters = {
  ds_start: "",
  ds_end: "",
  taskId: "",
  taskName: "",
  action_name: "",
  condition: "",
};

const DEFAULT_PAGE_SIZE = 20;

/** 筛选表单 → 查询入参（空串剔除为 undefined，不参与过滤）。 */
function filtersToQuery(
  f: LogFilters,
  page: number,
  pageSize: number,
): AutoAdOperateResultQuery {
  const q: AutoAdOperateResultQuery = { page, page_size: pageSize };
  if (f.ds_start) q.ds_start = f.ds_start;
  if (f.ds_end) q.ds_end = f.ds_end;
  if (f.taskId) q.taskId = f.taskId;
  if (f.taskName) q.taskName = f.taskName;
  if (f.action_name) q.action_name = f.action_name;
  if (f.condition) q.condition = f.condition;
  return q;
}

/** 操作类型 → 徽章语义色（LEO §6.5 状态徽章描边色）。 */
function actionTagClass(action_name: string): string {
  switch (action_name) {
    case "warning":
      return "leo-tag is-warning";
    case "stop_adgroup":
      return "leo-tag is-danger";
    case "set_budget":
    case "reduce_budget":
      return "leo-tag is-info";
    default:
      return "leo-tag is-info";
  }
}

/** 媒体交互状态 → 徽章语义色（success 绿 / fail 红）。 */
function statusTagClass(status: string): string {
  switch (status) {
    case "success":
      return "leo-tag is-success";
    case "fail":
      return "leo-tag is-danger";
    default:
      return "leo-tag is-info";
  }
}

export default function AutoAdOperateResultPage() {
  const [filters, setFilters] = useState<LogFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AutoAdOperateResultPage | null>(null);
  const [enums, setEnums] = useState<AutoAdOperateResultEnums>({
    action_name: [],
  });
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async (pageToLoad: number) => {
    setLoading(true);
    try {
      const result = await getAutoAdOperateResult(
        filtersToQuery(filters, pageToLoad, DEFAULT_PAGE_SIZE),
      );
      setData(result);
      setPage(pageToLoad);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取策略运行记录失败";
      pushToast(msg, "error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadEnums = useCallback(async () => {
    try {
      const e = await getAutoAdOperateResultEnums();
      setEnums(e);
    } catch (err) {
      // 枚举失败不阻塞主数据；下拉退化为空，仅提示。
      pushToast(
        err instanceof ApiError ? err.message : "下拉枚举拉取失败",
        "info",
      );
    }
  }, []);

  useEffect(() => {
    // 进入页面：先取枚举，再取首页（无条件）。
    let cancelled = false;
    (async () => {
      await loadEnums();
      if (cancelled) return;
      setLoading(true);
      try {
        const result = await getAutoAdOperateResult(
          filtersToQuery(EMPTY_FILTERS, 1, DEFAULT_PAGE_SIZE),
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          pushToast(
            err instanceof ApiError ? err.message : "拉取策略运行记录失败",
            "error",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadEnums]);

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.total / data.page_size));
  }, [data]);

  function updateFilter<K extends keyof LogFilters>(key: K, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function onSearch() {
    void reload(1);
  }

  function onReset() {
    setFilters(EMPTY_FILTERS);
    // 重置后立即查询（filters 状态异步更新，手动传空过滤 + page 1）。
    setLoading(true);
    getAutoAdOperateResult(filtersToQuery(EMPTY_FILTERS, 1, DEFAULT_PAGE_SIZE))
      .then((result) => {
        setData(result);
        setPage(1);
      })
      .catch((err) => {
        pushToast(
          err instanceof ApiError ? err.message : "拉取策略运行记录失败",
          "error",
        );
        setData(null);
      })
      .finally(() => setLoading(false));
  }

  const items: AutoAdOperateResultRecord[] = data?.items ?? [];

  return (
    <div className="mx-auto max-w-[1280px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5">
        <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
          策略运行记录
        </h1>
        <p className="mt-1 text-[12.5px] text-text-muted">
          华为商店 huawei_red_check 策略执行结果日志（t_huawei_auto_ad_operate_result）；
          含媒体交互状态与响应报文，支持按操作日期、任务ID、任务名称、操作类型、操作原因筛选查询。
        </p>
      </div>

      {/* 筛选条 */}
      <div className="leo-card mb-4 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作日期 起
            </label>
            <input
              type="date"
              className="leo-input w-[150px]"
              value={filters.ds_start}
              onChange={(e) => updateFilter("ds_start", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作日期 止
            </label>
            <input
              type="date"
              className="leo-input w-[150px]"
              value={filters.ds_end}
              onChange={(e) => updateFilter("ds_end", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              任务ID
            </label>
            <input
              type="text"
              className="leo-input w-[150px]"
              placeholder="模糊匹配"
              value={filters.taskId}
              onChange={(e) => updateFilter("taskId", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              任务名称
            </label>
            <input
              type="text"
              className="leo-input w-[150px]"
              placeholder="模糊匹配"
              value={filters.taskName}
              onChange={(e) => updateFilter("taskName", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作类型
            </label>
            <select
              className="leo-select w-[170px]"
              value={filters.action_name}
              onChange={(e) => updateFilter("action_name", e.target.value)}
            >
              <option value="">全部</option>
              {enums.action_name.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作原因
            </label>
            <input
              type="text"
              className="leo-input w-[200px]"
              placeholder="模糊匹配"
              value={filters.condition}
              onChange={(e) => updateFilter("condition", e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSearch}
              disabled={loading}
              className="leo-cta-primary w-auto px-7"
            >
              {loading ? "查询中..." : "查询"}
            </button>
            <button
              type="button"
              onClick={onReset}
              disabled={loading}
              className="leo-btn-ghost"
            >
              重置
            </button>
          </div>
        </div>
      </div>

      {/* 表格 */}
      <div className="leo-card rounded-xl p-4">
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div className="text-[13px] text-text-muted">加载中…</div>
          </div>
        ) : items.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div className="text-[13px] text-text-muted">无策略运行记录</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="border-b border-slate-200 text-text-muted">
                  <th className="px-3 py-2 text-left font-medium">任务ID</th>
                  <th className="px-3 py-2 text-left font-medium">任务名称</th>
                  <th className="px-3 py-2 text-left font-medium">操作类型</th>
                  <th className="px-3 py-2 text-left font-medium">操作原因</th>
                  <th className="px-3 py-2 text-left font-medium">媒体交互</th>
                  <th className="px-3 py-2 text-left font-medium">媒体响应报文</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap min-w-[160px]">操作时间</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={`${r.taskId}-${r.action_name}-${r.created_time}`}
                    className="border-b border-slate-200 align-top text-text-strong"
                  >
                    <td className="px-3 py-2 font-mono text-text-emphasis">
                      {r.taskId || "—"}
                    </td>
                    <td
                      className="px-3 py-2 text-text-body"
                      title={r.taskName}
                    >
                      <div className="max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis">
                        {r.taskName || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={actionTagClass(r.action_name)}>
                        {r.action_name || "—"}
                      </span>
                    </td>
                    <td
                      className="px-3 py-2 text-text-body"
                      title={r.condition}
                    >
                      <div className="max-w-[360px] break-words line-clamp-2">
                        {r.condition || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {r.status ? (
                        <span className={statusTagClass(r.status)}>
                          {r.status}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      className="px-3 py-2 text-text-body"
                      title={r.message}
                    >
                      <div className="max-w-[260px] whitespace-nowrap overflow-hidden text-ellipsis">
                        {r.message || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-text-body whitespace-nowrap">
                      {r.created_time || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页 */}
        {!loading && data && data.total > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-[12px] text-text-muted">
              共 <span className="text-text-emphasis">{data.total}</span> 条 · 第{" "}
              <span className="text-text-emphasis">{page}</span>/{totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void reload(page - 1)}
                disabled={page <= 1 || loading}
                className="leo-btn-ghost"
              >
                上一页
              </button>
              <button
                type="button"
                onClick={() => void reload(page + 1)}
                disabled={page >= totalPages || loading}
                className="leo-btn-ghost"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
